import { useState } from 'react';
import {
  Layout,
  Card,
  List,
  Typography,
  Space,
  Select,
  Button,
  Tag,
  Empty,
  Row,
  Col,
  message,
} from 'antd';
import * as AntIcons from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useAuth } from '@/contexts/AuthContext';
import { useNotifications } from '@/hooks/useNotifications';
import { NotificationRecord, NotificationType, NotificationPriority } from '@/types/notifications';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { Option } = Select;

export default function NotificationsPage() {
  const { t } = useTranslation(['notifications', 'common']);
  const { firestoreUser } = useAuth();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const navigate = useNavigate();
  
  const [selectedType, setSelectedType] = useState<NotificationType | 'all'>('all');
  const [selectedPriority, setSelectedPriority] = useState<NotificationPriority | 'all'>('all');
  const [isMarkingAll, setIsMarkingAll] = useState(false);

  // Filter notifications based on selected filters
  const filteredNotifications = notifications.filter(notification => {
    const typeMatch = selectedType === 'all' || notification.type === selectedType;
    const priorityMatch = selectedPriority === 'all' || notification.priority === selectedPriority;
    return typeMatch && priorityMatch;
  });

  // Get unique types and priorities for filter options
  const availableTypes = Array.from(new Set(notifications.map(n => n.type)));
  const availablePriorities = Array.from(new Set(notifications.map(n => n.priority)));

  const handleMarkAllAsRead = async () => {
    setIsMarkingAll(true);
    try {
      await markAllAsRead();
      message.success(t('notifications:messages.markAllSuccess'));
    } catch (error) {
      message.error(t('notifications:messages.markAllError'));
    } finally {
      setIsMarkingAll(false);
    }
  };

  const handleNotificationClick = async (notification: NotificationRecord) => {
    const isRead = firestoreUser?.id ? notification.readBy[firestoreUser.id] !== undefined : false;
    
    if (!isRead) {
      await markAsRead(notification.id);
    }
    
    // Navigate to action URL if available
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
  };

  const getTypeColor = (type: NotificationType): string => {
    switch (type) {
      case 'payment': return '#28a745';
      case 'maintenance': return '#17a2b8';
      case 'visitor': return '#ffc107';
      case 'announcement': return '#007bff';
      case 'message': return '#6c757d';
      case 'security': return '#dc3545';
      case 'new_message_daily': return '#9c27b0';
      case 'community_event': return '#ff9800';
      case 'facility_booking': return '#4caf50';
      case 'emergency_alert': return '#f44336';
      case 'system_update': return '#607d8b';
      default: return '#6c757d';
    }
  };

  const getPriorityColor = (priority: NotificationPriority): string => {
    switch (priority) {
      case 'high': return '#dc3545';
      case 'medium': return '#ffc107';
      case 'low': return '#28a745';
      default: return '#6c757d';
    }
  };

  return (
    <Layout style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', width: '100%' }}>
        {/* Header */}
        <Card style={{ marginBottom: 24 }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={2} style={{ margin: 0 }}>
                {t('notifications:title')}
              </Title>
              <Text type="secondary">
                {unreadCount > 0 ? t('notifications:unreadCount', { count: unreadCount }) : t('notifications:subtitle')}
              </Text>
            </Col>
            <Col>
              <Space>
                {unreadCount > 0 && (
                  <Button
                    type="primary"
                    icon={<AntIcons.CheckOutlined />}
                    onClick={handleMarkAllAsRead}
                    loading={isMarkingAll}
                  >
                    {t('notifications:markAllAsRead')}
                  </Button>
                )}
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Filters */}
        <Card style={{ marginBottom: 24 }}>
          <Row gutter={16} align="middle">
            <Col>
              <Text strong>{t('notifications:filters.filterBy')}</Text>
            </Col>
            <Col>
              <Select
                value={selectedType}
                onChange={setSelectedType}
                style={{ width: 200 }}
                placeholder={t('notifications:filters.allTypes')}
              >
                <Option value="all">{t('notifications:filters.allTypes')}</Option>
                {availableTypes.map(type => (
                  <Option key={type} value={type}>
                    {t(`notifications:types.${type}`)}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col>
              <Select
                value={selectedPriority}
                onChange={setSelectedPriority}
                style={{ width: 150 }}
                placeholder={t('notifications:filters.allPriorities')}
              >
                <Option value="all">{t('notifications:filters.allPriorities')}</Option>
                {availablePriorities.map(priority => (
                  <Option key={priority} value={priority}>
                    {t(`notifications:priorities.${priority}`)}
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>
        </Card>

        {/* Notifications List */}
        <Card>
          {filteredNotifications.length === 0 ? (
            <Empty
              description={t('notifications:empty.noNotifications')}
              style={{ padding: '48px 0' }}
            />
          ) : (
            <List
              dataSource={filteredNotifications}
              renderItem={(notification: NotificationRecord) => {
                const isRead = firestoreUser?.id ? notification.readBy[firestoreUser.id] !== undefined : false;
                const isNewMessage = notification.type === 'new_message_daily';
                
                return (
                  <List.Item
                    style={{
                      padding: '16px 0',
                      backgroundColor: isRead ? 'transparent' : '#f6ffed',
                      borderRadius: '8px',
                      marginBottom: '8px',
                      cursor: 'pointer',
                      border: isRead ? '1px solid #f0f0f0' : '1px solid #b7eb8f',
                      transition: 'all 0.2s ease'
                    }}
                    onClick={() => handleNotificationClick(notification)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = isRead ? '#fafafa' : '#f0f9ff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = isRead ? 'transparent' : '#f6ffed';
                    }}
                  >
                    <List.Item.Meta
                      avatar={
                        <div style={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: getTypeColor(notification.type),
                          marginTop: 8
                        }} />
                      }
                      title={
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div>
                            <Text strong={!isRead} style={{ 
                              fontSize: '16px',
                              color: isRead ? '#666' : '#000'
                            }}>
                              {notification.title}
                            </Text>
                            {!isRead && (
                              <Tag color="green" style={{ marginLeft: 8 }}>
                                {t('notifications:notification.new')}
                              </Tag>
                            )}
                          </div>
                          <Tag color={getPriorityColor(notification.priority)}>
                            {t(`notifications:priorities.${notification.priority}`)}
                          </Tag>
                        </div>
                      }
                      description={
                        <div>
                          <div style={{ 
                            fontSize: '14px', 
                            color: '#666',
                            marginBottom: 8,
                            lineHeight: '1.4'
                          }}>
                            {notification.title}
                          </div>
                          
                          {/* Message preview for new_message_daily notifications */}
                          {isNewMessage && (
                            <div style={{
                              fontSize: '12px',
                              color: '#666',
                              fontStyle: 'italic',
                              backgroundColor: '#f5f5f5',
                              padding: '8px 12px',
                              borderRadius: '6px',
                              marginBottom: 8,
                              border: '1px solid #e8e8e8'
                            }}>
                              <Text type="secondary">{t('notifications:notification.messagePreview')}</Text>
                              <br />
                              "{notification.title}"
                            </div>
                          )}
                          
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            fontSize: '12px',
                            color: '#999'
                          }}>
                            <div>
                              <Tag color={getTypeColor(notification.type)}>
                                {t(`notifications:types.${notification.type}`)}
                              </Tag>
                              <span style={{ marginLeft: 8 }}>
                                {dayjs(notification.timestamp.toDate()).format('MMM D, YYYY h:mm A')}
                              </span>
                            </div>
                            {notification.actionUrl && (
                              <Button 
                                type="link" 
                                size="small"
                                icon={<AntIcons.ArrowRightOutlined />}
                                style={{ padding: 0, height: 'auto' }}
                              >
                                {t('notifications:notification.view')}
                              </Button>
                            )}
                          </div>
                        </div>
                      }
                    />
                  </List.Item>
                );
              }}
            />
          )}
        </Card>
      </div>
    </Layout>
  );
}
